// Custo de funcionário do MEI — "quanto custa contratar 1 ou 2 pessoas?"
// LC 238/2026: a partir de 1º/01/2027 o MEI pode ter até 2 empregados (antes: 1),
// cada um recebendo exclusivamente 1 salário mínimo ou o piso da categoria.
// Encargos do MEI empregador (LC 123, art. 18-C): 3% de CPP (INSS patronal) + 8% de FGTS.

import { getLimiteMei } from './constantes'

/** INSS patronal (CPP) que o MEI recolhe sobre a remuneração do empregado. */
export const CPP_MEI = 0.03
/** FGTS sobre a remuneração. */
export const FGTS = 0.08
/** Desconto máximo de vale-transporte do salário do empregado. */
export const DESCONTO_VT = 0.06
/** Limite de empregados por ano (LC 123, art. 18-C; LC 238/2026 a partir de 2027). */
export function maxFuncionariosMei(ano: number): number {
  return ano >= 2027 ? 2 : 1
}

export interface CustoFuncionarioInputs {
  /** Quantos empregados (1 ou 2). */
  funcionarios: number
  /** Salário bruto de cada um (mínimo ou piso). */
  salario: number
  /** Gasto mensal com vale-transporte de cada um (o que a empresa compra). */
  valeTransporte?: number
  /** Faturamento médio do mês — opcional, pra mostrar o peso da folha. */
  faturamentoMes?: number
  /** Ano de referência do teto (pra comparar o custo anual). */
  ano?: number
}

export interface CustoFuncionarioResultado {
  /** Por empregado: encargos mensais (CPP + FGTS) sobre o salário. */
  encargosMes: number
  /** Por empregado: o que a empresa banca de VT (compra − 6% descontado do salário). */
  valeTransporteEmpresa: number
  /** Por empregado: saída de caixa do mês (salário + encargos + VT). */
  caixaMes: number
  /** Por empregado: 13º + 1/3 de férias (já com encargos), divididos por 12. */
  provisaoMes: number
  /** Por empregado: custo real do mês (caixa + provisão). */
  custoRealMes: number
  /** Total da folha (todos os empregados), custo real do mês. */
  totalMes: number
  /** Total do ano: 12 salários (um deles é o das férias) + 13º + 1/3 de férias, com encargos e VT. */
  totalAno: number
  /** % do faturamento do mês comprometido com a folha (null sem faturamento). */
  percentFaturamento: number | null
  /** % do teto anual do MEI que a folha consome. */
  percentTeto: number
  limiteAno: number
  /** Ano a partir do qual a quantidade pedida é permitida. */
  permitidoAPartirDe: number
}

const round2 = (n: number) => Number(n.toFixed(2))

export function calcularCustoFuncionario(inputs: CustoFuncionarioInputs): CustoFuncionarioResultado {
  const funcionarios = Math.min(Math.max(Math.round(inputs.funcionarios || 1), 1), 2)
  const salario = Math.max(inputs.salario || 0, 0)
  const vt = Math.max(inputs.valeTransporte || 0, 0)
  const ano = inputs.ano ?? 2027
  const limiteAno = getLimiteMei(ano)

  const fatorEncargos = CPP_MEI + FGTS
  const encargosMes = salario * fatorEncargos
  const valeTransporteEmpresa = Math.max(0, vt - salario * DESCONTO_VT)
  const caixaMes = salario + encargosMes + valeTransporteEmpresa

  // Por cima dos 12 salários do ano (o mês de férias já é um deles): 13º (1 salário)
  // e o terço de férias (1/3 de salário), ambos com CPP + FGTS → 13,33 salários/ano.
  const decimoTerceiroAno = salario * (1 + fatorEncargos)
  const tercoFeriasAno = (salario / 3) * (1 + fatorEncargos)
  const provisaoMes = (decimoTerceiroAno + tercoFeriasAno) / 12

  const custoRealMes = caixaMes + provisaoMes
  const totalMes = custoRealMes * funcionarios
  // No mês de férias o VT não é pago — 11 meses de VT no ano.
  const totalAno =
    (salario * 12 * (1 + fatorEncargos) + decimoTerceiroAno + tercoFeriasAno + valeTransporteEmpresa * 11) *
    funcionarios

  const fat = inputs.faturamentoMes || 0

  return {
    encargosMes: round2(encargosMes),
    valeTransporteEmpresa: round2(valeTransporteEmpresa),
    caixaMes: round2(caixaMes),
    provisaoMes: round2(provisaoMes),
    custoRealMes: round2(custoRealMes),
    totalMes: round2(totalMes),
    totalAno: round2(totalAno),
    percentFaturamento: fat > 0 ? round2((totalMes / fat) * 100) : null,
    percentTeto: round2((totalAno / limiteAno) * 100),
    limiteAno,
    permitidoAPartirDe: funcionarios > 1 ? 2027 : 2026,
  }
}
